import {
	fetchCoursesDropdown,
	fetchUnitsDropdown,
} from '@Features/dropdowns/dropdownsSlice';
import { useTypedSelector } from '@Features/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, MenuItem, TextField } from '@mui/material';
import { FlexLayout } from '@Styled/utilities';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { number, object, string } from 'yup';
import { DropdownUnit } from '@Features/dropdowns/dropdownsSlice.types';
import { submissionsGetAll } from '../../Slices/Submissions.slice';
import { Button } from '@mui/material';
import { SortableList } from '../../Constants/Table.constants';

const schema = object({
	course: number(),
	test: number(),
});

const SortableSchema = object({
	sort: string(),
});

const TableDropdowns = () => {
	const dispatch = useDispatch();
	const [allTests, setAllTests] = useState<Array<DropdownUnit> | undefined>([]);
	const { filters, meta, query, dateRange, sortBy } = useTypedSelector(
		(state) => state.Submissions
	);
	const { currentClassroom } = useTypedSelector((state) => state.classroom);
	const {
		control,
		formState: { dirtyFields, isDirty, isValid },
		reset,
		watch,
		setValue,
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			course: null,
			test: null,
		},
	});
	const {
		control: controlSort,
		reset: resetSort,
		watch: watchSort,
		formState: sortFormState,
	} = useForm({
		resolver: yupResolver(SortableSchema),
		defaultValues: {
			sort: null,
		},
	});

	const course = watch('course');
	const test = watch('test');
	const sort = watchSort('sort');

	const courses = useTypedSelector((state) => state.dropdowns.dropdownCourses);
	const tests = useTypedSelector((state) => state.dropdowns.DropdownUnits);

	useEffect(() => {
		if (currentClassroom?.id) {
			dispatch(fetchCoursesDropdown(currentClassroom?.id));
		}
	}, [currentClassroom?.id]);

	const getUnitsTest = async () => {
		if (course) dispatch(fetchUnitsDropdown(`${course}?type=test` as any));
	};

	useEffect(() => {
		getUnitsTest();
	}, [course]);

	useEffect(() => {
		if (tests) {
			setAllTests(tests);
		}
	}, [tests]);

	useEffect(() => {
		if (course || test) {
			dispatch(
				submissionsGetAll({
					page: meta?.current_page as number,
					filters: filters,
					perPage: meta?.per_page ?? 10,
					query: query as string,
					sortBy: sortBy,
					from: dateRange.from,
					to: dateRange.to,
					classroom_id: currentClassroom?.id,
					course_id: course as unknown as number,
					unit_id: test as unknown as number,
				})
			);
		}
	}, [course, test]);

	useEffect(() => {
		if (sort) {
			dispatch(
				submissionsGetAll({
					page: meta?.current_page as number,
					filters: filters,
					perPage: meta?.per_page ?? 10,
					query: query as string,
					sortBy: { direction: sort, field: 'created_at' },
					from: dateRange.from,
					to: dateRange.to,
					classroom_id: currentClassroom?.id,
					course_id: course ? Number(course) : null,
					unit_id: test ? Number(test) : null,
				})
			);
		}
	}, [sort]);

	const resetFilter = () => {
		reset();
		resetSort();
		dispatch(
			submissionsGetAll({
				page: meta?.current_page as number,
				filters: filters,
				perPage: meta?.per_page ?? 10,
				query: query as string,
				sortBy: null,
				from: dateRange.from,
				to: dateRange.to,
				classroom_id: currentClassroom?.id,
			})
		);
	};

	return (
		<Container>
			<FlexLayout gridGap="0.8rem">
				<Controller
					control={control}
					name="course"
					render={({ field: { onChange, ...others } }) => (
						<FormControl variant="outlined">
							<TextFieldStyle
								select
								{...others}
								onChange={(e) => {
									setValue('test', null);
									onChange(e.target.value);
								}}
								size="small"
								label={course ? null : 'Select course'}
							>
								{courses?.map((cr, index) => (
									<MenuItem key={`${cr.name}-${index}`} value={cr.id}>
										{cr.name}
									</MenuItem>
								))}
							</TextFieldStyle>
						</FormControl>
					)}
				/>
				<Controller
					control={control}
					name="test"
					render={({ field: { onChange, ...others } }) => (
						<TextFieldStyle
							size="small"
							select
							{...others}
							label={test ? null : 'Select Test'}
							error={dirtyFields.course && allTests && allTests?.length <= 0}
							helperText={
								dirtyFields.course &&
								allTests &&
								allTests.length <= 0 &&
								'No tests found'
							}
							onChange={(e) => {
								onChange(e.target.value);
							}}
						>
							{allTests &&
								allTests?.map((test, index) => {
									return (
										<MenuItem key={`${test?.name}-${index}`} value={test?.id}>
											{test?.name}
										</MenuItem>
									);
								})}
						</TextFieldStyle>
					)}
				/>
				<Controller
					control={controlSort}
					name="sort"
					render={({ field: { onChange, ...others } }) => (
						<TextFieldStyle
							size="small"
							select
							{...others}
							label={sort ? null : 'Sort by Att. Date'}
							error={SortableList?.length <= 0}
							onChange={(e) => {
								onChange(e.target.value);
							}}
						>
							{SortableList?.map((sort, index) => {
								return (
									<MenuItem
										key={`${sort?.value}-${index}`}
										value={sort?.direction}
									>
										{sort?.value}
									</MenuItem>
								);
							})}
						</TextFieldStyle>
					)}
				/>
			</FlexLayout>
			<ButtonsWrapper gridGap="1.8rem">
				<Button
					disabled={isDirty ? !isDirty : !sortFormState.isDirty}
					color="warning"
					onClick={() => {
						resetFilter();
					}}
				>
					Reset
				</Button>
			</ButtonsWrapper>
		</Container>
	);
};

const Container = styled(FlexLayout)`
	position: absolute;
	padding: 0rem 1rem;
	gap: 0.8rem;
	flex-direction: column;
`;
const ButtonsWrapper = styled(FlexLayout)`
	z-index: 999;
`;
const TextFieldStyle = styled(TextField)`
	width: 11.875rem;
`;

export default TableDropdowns;
